import { useState } from "react";
import axios from "axios";
import actions from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import userActionTypes from ".././../actions/types/user.action.type";
import availableDocumentTypes from "../../actions/types/availableDocument.action.type";
import { useTranslation } from "react-i18next";
import billerProfileTypes from "../../actions/types/billerProfile.action.type";
import moment from "moment";
import { listProps, requiredFieldError, invalidFieldError, sanitizeQueryParams, formatDateOrReplace, checkFieldValue } from "../../utils/helper";
import { Regex, FI } from "../../constants/sharedConstants.js";

export const useCustomHook = ({
  initialStates,
  tableHeadings,
  tableResults,
  setErrorMessage,
  errorMessage,
  userRoleList,
  setLoading,
  setInitialLoading,
  propsComp,
  setUserData,
  setErrorMssg,
  setManageUserState,
  pageName,
  userDetails,
  setPricePlaneData,
  billerId,
  centralizedBillerId,
  roleId,
  loginUser,
  setPageNumber,
  setRowPerPage,
  rowPerPage,
  pageNumber,
  setHighlightError,
  fiNumber,
  minDateValue,
  maxDateValue,
  handleCloseSuccessMessage,
  hasSuccessMessageAvailable,
  pageLimitSize,
  extraInputValiation
}) => {
  const [searchCriteria, setSearchCriteria] = useState(initialStates);
  const [actualRequest, setActualRequest] = useState({...initialStates, offset: 0, limit: 25});
  const [isSearchValid, setIsSearchValid] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [hasSearch, setHasSearch] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pricePlaneResult = useSelector(
    (state) => pageName === "pricePlan" ? state?.pricePlanReducer?.pricePlanList : {}
  );

  const gridData = {
    columns: tableHeadings,
    rows: [],
    count: 0,
  };

  const searchCriteriaChangeHandler = (field) => (event) => {
    if (field === "startDate" || field === "endDate") {
      setSearchCriteria({ ...searchCriteria, [field]: event });
    } else if (
      pageName === "manageUser" ||
      pageName === "pricePlan" ||
      pageName === "manageBudget"
    ) {
      setManageUserState({ ...userDetails, [field]: event.target.value });
    } else {
      setSearchCriteria({ ...searchCriteria, [field]: event.target.value });
    }
  };

  const searchCriteriaBlurHandler = (field) => (event) => {
    let upperValue = "";
    if (event?.target?.value && typeof event.target.value === "string") {
      upperValue = event.target.value.toUpperCase();
    }
    if (field === "startDate" || field === "endDate") {
      setSearchCriteria({ ...searchCriteria, [field]: event });
    } else if (
      pageName === "manageUser" ||
      pageName === "pricePlan" ||
      pageName === "manageBudget"
    ) {
      setManageUserState({ ...userDetails, [field]: event.target.value });
    } else {
      setSearchCriteria({ ...searchCriteria, [field]: upperValue });
    }
  };

  const hasSearchValue = () => {
    const searchValues =
      pageName === "manageUser" ||
      pageName === "pricePlan" ||
      pageName === "manageBudget"
        ? userDetails
        : searchCriteria;
    const keys = Object.keys(searchValues);
    return !keys.some(
      (key) => typeof searchValues[key] === "string" && searchValues[key].trim()
    );
  };

  /** Returns the current search criteria object for the page (used by hasSearchValue and empty-criteria highlight). */
  // Ideally every page should use searchCriteria object for search criteria, but manageUser page uses userDetails object for search criteria.
  const getSearchValues = () =>
    pageName === "manageUser" ||
    pageName === "pricePlan" ||
    pageName === "manageBudget"
      ? userDetails
      : searchCriteria;

  /** Keys that are not search input fields (pagination/metadata) - exclude from "highlight all" when empty. */
  const EMPTY_CRITERIA_EXCLUDE_KEYS = ["startOffset", "endOffset", "count", "offset", "limit", "isWebportalReport"];

  /** Builds fieldErrors to highlight all search fields when "at least one search criteria must be provided." */
  const getEmptyCriteriaHighlightErrors = (searchValues) => {
    if (!searchValues || typeof searchValues !== "object") return {};
    return Object.keys(searchValues).reduce((acc, key) => {
      if (EMPTY_CRITERIA_EXCLUDE_KEYS.includes(key)) return acc;
      return { ...acc, [key]: true };
    }, {});
  };

  const resetHandler = () => {
    setIsSearchValid(true);
    setSearchResults(null);
    setManageUserState(initialStates);
    dispatch({ type: userActionTypes.GET_USER_DETAIL_SUCCESS, data: [] });
    setErrorMessage([]);
    setFieldErrors({});
    if (setUserData) {
      setUserData(null);
    }
  };

  const resetBudgetCategoryHandler = () => {
    setIsSearchValid(true);
    dispatch(
      actions.budgetCategoryActions.getBudgetCategory(0, "", "", 25, 0, t, 25)
    );
    setManageUserState(initialStates);
    setSearchCriteria(initialStates);
  };

  const resetPricePlanHandler = () => {
    setIsSearchValid(true);
    dispatch(actions.pricePlanActions.getPricePlanList());
    setManageUserState(initialStates);
    setErrorMssg(null);
    setRowPerPage(pageLimitSize);
    setPageNumber(0);
  };

  const initiateSearch = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    axios
      .get(`https://www.erps-backend.com`, {
        params: { ...actualCriteria },
      })
      .then((response) => {
        setSearchResults({
          ...gridData,
          rows: [...response.data.rows],
          count: response.data.count,
        });
      })
      .catch((err) => {
        //TODO - error to be handled here and below rendering to be removed
        setSearchResults({
          ...gridData,
          rows: [
            ...tableResults.data.slice(
              actualCriteria.startOffset,
              actualCriteria.endOffset
            ),
          ],
          count: tableResults.count,
        });
      });
  };

  const paginateBudgetCategories = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const budgetCategoryResultCount = tableResults.count;
    const pagSize = actualCriteria.endOffset - actualCriteria.startOffset;
    if (actualCriteria.startOffset < budgetCategoryResultCount) {
      setManageUserState((prevState) => ({
        ...prevState,
        startOffset: actualCriteria.startOffset,
        endOffset: actualCriteria.endOffset,
      }));
      setInitialLoading(true);
      dispatch(
        actions.budgetCategoryActions.getBudgetCategory(
          budgetCategoryResultCount,
          "",
          "",
          pagSize,
          actualCriteria.startOffset,
          t,
          actualCriteria.endOffset,
          setInitialLoading
        )
      );
    }
  };

  const searchBudgetCategoryByParams = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const budgetCode = actualCriteria.budgetCode
      ? actualCriteria.budgetCode
      : "";
    const description = actualCriteria.description
      ? actualCriteria.description
      : "";
    dispatch(
      actions.budgetCategoryActions.getBudgetCategory(
        0,
        budgetCode,
        description,
        25,
        0,
        t,
        25
      )
    );
  };

  const paginateBillerProfilesList = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const billerProfileResultCount = tableResults.count;
    const billerId = searchCriteria.billerId ? searchCriteria.billerId : "";
    const billerName = searchCriteria.billerName || "";
    const pagSize = actualCriteria.endOffset - actualCriteria.startOffset;
    if (actualCriteria.startOffset < billerProfileResultCount)
      dispatch(
        actions.billerProfileActions.getBillerProfiles(
          billerProfileResultCount,
          billerId,
          billerName,
          pagSize,
          actualCriteria.startOffset,
          t
        )
      );
  };

  const searchBillerProfileByParams = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const billerId = actualCriteria.billerId ? actualCriteria.billerId : "";
    const billerName = actualCriteria.billerName
      ? actualCriteria.billerName
      : "";
    dispatch(
      actions.billerProfileActions.getBillerProfiles(
        0,
        billerId,
        billerName,
        25,
        0,
        t,
        setLoading
      )
    );
  };

  const resetBillerProfileHandler = () => {
    setIsSearchValid(true);
    dispatch({
      type: billerProfileTypes.GET_BILLER_PROFILE_LIST_SUCCESS,
      data: null,
    });
    setSearchCriteria(initialStates);
    setFieldErrors({})
    setErrorMssg(t("erps.billermanagement.filterbillercategory.error.emptyinput"));
  };

  const searchPricePlanByParams = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const pricePlanId = actualCriteria.pricePlanId
      ? actualCriteria.pricePlanId
      : "";
    const pricePlanName = actualCriteria.pricePlanName
      ? actualCriteria.pricePlanName
      : "";
    let errors = [];
    const numericRegEx = /^[0-9]+$/;
    if (pricePlanId && !numericRegEx.test(pricePlanId)) {
      errors.push(t("erps.managePricePlan.priceplanid.error.numeric"));
    }

    if (errors && errors.length) {
      if (setLoading) {
        setLoading(false);
      }
      setErrorMssg(errors);
    } else {
      if (errorMessage && errorMessage.length) {
        setErrorMssg([]);
      }
      dispatch(
        actions.pricePlanActions.getFilteredPricePlan({
          pricePlanId,
          pricePlanName,
          setLoading,
          setPricePlaneData,
        })
      );
    }
  };

  const handleBudgetCategorySearch = () => {
    if (hasSearchValue()) {
      setIsSearchValid(false);
      setSearchResults(null);
      setFieldErrors(getEmptyCriteriaHighlightErrors(getSearchValues()));
    } else {
      setIsSearchValid(true);

      //TODO - Below if condition to be removed [Once actual api is available]
      //if (checkSearchValue()) {
      searchBudgetCategoryByParams({ ...userDetails });
      //} else {
      //setSearchResults({ count: 0, rows: [] });
      //}
    }
  };

  const handleBillerProfileSearch = () => {
    if (hasSearchValue()) {
      setIsSearchValid(false);
      dispatch({
        type: billerProfileTypes.GET_BILLER_PROFILE_LIST_SUCCESS,
        data: null,
      });
      setErrorMssg(t("erps.billermanagement.filterbillercategory.error.emptyinput"));
      setFieldErrors(getEmptyCriteriaHighlightErrors(getSearchValues()));
    } else {
      const billerName = searchCriteria?.billerName?.trim();
      const billerId = searchCriteria?.billerId?.trim();
      const numericRegEx = /^[0-9]+$/;
      const alphaNumericRegEx = /^[0-9A-Za-z ]+$/;
      const err = [];
      let highlightErrorV1 = {};
      setIsSearchValid(true);

      if (
        billerName &&
        billerName.includes("%") &&
        billerName.length < 3 &&
        setErrorMssg
      ) {
        err.push(
          t(
            "erps.billermanagement.filterbillercategory.error.invaliduseofwildcardcharacter"
          )
        );
        highlightErrorV1 = { ...highlightErrorV1, billerName: true };
      }
      if (billerId && fiNumber != 50 && !alphaNumericRegEx.test(billerId)) {
        err.push(
          t(
            "erps.billermanagement.filterbillercategory.error.billerIdAlphaNumeric"
          )
        );
        highlightErrorV1 = { ...highlightErrorV1, billerId: true };
      } else if (billerId && fiNumber == 50 && !numericRegEx.test(billerId)) {
        err.push(
          t("erps.billermanagement.filterbillercategory.error.invalidBillerid")
        );
        highlightErrorV1 = { ...highlightErrorV1, billerId: true };
      }
      if (err.length) {
        setErrorMssg(err);
        setSearchResults(null);
        setFieldErrors(highlightErrorV1);
      } else {
        setErrorMssg(null);
        setFieldErrors({});
        if (setLoading) {
          setLoading(true);
        }
        //TODO - Below if condition to be removed [Once actual api is available]
        //if (checkSearchValue()) {
        searchBillerProfileByParams({ ...searchCriteria });
        //} else {
        //setSearchResults({ count: 0, rows: [] });
        //}
      }
    }
  };

  const handleAvailableDocumentsSearch = (allSourceReplacement, overrideSelectedFi) => { // only called from AvailableDocument.js (as of 2025-Apr-10)

    let validationResult;
    if (searchCriteria?.startDate || searchCriteria?.endDate) {
      setIsSearchValid(true);
      validationResult = validateStartEndDates(searchCriteria?.startDate, searchCriteria?.endDate);
    }
    if (hasSearchValue()) {
      setIsSearchValid(false);
      setSearchResults(null);
      setHasSearch(false);
      setErrorMessage([]);
      setHighlightError({});
    } else if (validationResult?.errors.length) {
      setErrorMessage(validationResult?.errors);
      setHighlightError(validationResult?.highlightError);
      setIsSearchValid(true);
    } else {
      setIsSearchValid(true);
      setErrorMessage([]);
      setHighlightError({});
      setHasSearch(true);
      let request = { ...sanitizeQueryParams(searchCriteria), offset: 0, limit: actualRequest.limit };
      if (overrideSelectedFi)
        request.selectedFiNumber = overrideSelectedFi;
      if (!request.source && allSourceReplacement)
        request.source = allSourceReplacement;
      searchAvailableDocumentsByParams(request);
    }
  };

  const searchAvailableDocumentsByParams = (queryParams) => {
    const searchBillerFilesDto = {
      biller_id: propsComp.billerId ? propsComp.billerId : "",
      biller_name: propsComp.billerName ? propsComp.billerName : "",
      start_date: formatDateOrReplace(queryParams.startDate, "YYYY-MM-DD"),
      end_date: formatDateOrReplace(queryParams.endDate, "YYYY-MM-DD"),
      start_off_set: 0,
      limit: queryParams.limit,
      source: queryParams.source
    };
    if (propsComp.centralizedBillerId != null) {
      searchBillerFilesDto.ctb_id = propsComp.centralizedBillerId
    }
    if (queryParams.selectedFiNumber)
      searchBillerFilesDto.selected_fi_number = queryParams.selectedFiNumber;
    setActualRequest(searchBillerFilesDto);

    if (setPageNumber)
      setPageNumber(0);

    loadAvailableDocuments(searchBillerFilesDto);
  };

  const loadAvailableDocuments = (searchBillerFilesDto) => {
    if (setLoading)
      setLoading(true);
    if (setInitialLoading)
      setInitialLoading(true);
    setHasSearch(true);

    dispatch(
      actions.availableDocumentActions.getAvailableDocument(
        searchBillerFilesDto,
        setInitialLoading,
        setLoading
      )
    );
  };

  const paginateAvailableDocuments = (paginationParams) => {
    standardPaginate(paginationParams);
    loadAvailableDocuments(actualRequest);
  };

  const standardPaginate = (paginationParams) => {
    const { startOffset, endOffset } = paginationParams;
    actualRequest.start_off_set = startOffset;
    if (actualRequest.limit !== (endOffset - startOffset)) { // if the user changed the number of rows per page
      actualRequest.limit = endOffset - startOffset;
      if (setRowPerPage)
        setRowPerPage(actualRequest.limit);
    }
    if (setPageNumber)
      setPageNumber(endOffset / (endOffset - startOffset) - 1);
  };

  const validateStartEndDates = (startDate, endDate) => {
    let validationResult = { errors: [], highlightError: {} };
    const minMoment = moment.utc(minDateValue);
    const maxMoment = moment.utc(maxDateValue);
    const startMoment = moment.utc(startDate);
    const endMoment = moment.utc(endDate);

    if (startDate == "Invalid date")
      markError(validationResult, "startDate", invalidFieldError("erps.availabledocuments.page.startdate"));
    if (endDate == "Invalid date")
      markError(validationResult, "endDate", invalidFieldError("erps.availabledocuments.page.enddate"));

    if (startDate && !endDate) {
      markError(validationResult, "endDate", requiredFieldError("erps.availabledocuments.page.enddate"));
    } else if (endDate && !startDate) {
      markError(validationResult, "startDate", requiredFieldError("erps.availabledocuments.page.startdate"));
    }

    if (startMoment.isValid()) {
      if (minMoment.isValid() && startMoment.isBefore(minMoment))
        markError(validationResult, "startDate",
          t("erps.common.errorMessage.notBeforeMinimalDate", {fieldLabel: t("erps.availabledocuments.page.startdate"), minimalDate: minMoment.format("YYYY-MM-DD")}));
      if (maxMoment.isValid() && startMoment.isAfter(maxMoment))
        markError(validationResult, "startDate",
          t("erps.common.errorMessage.notAfterMaximalDate", {fieldLabel: t("erps.availabledocuments.page.startdate"), maximalDate: maxMoment.format("YYYY-MM-DD")}));
    }

    if (endMoment.isValid()) {
      if (minMoment.isValid() && endMoment.isBefore(minMoment))
        markError(validationResult, "endDate",
          t("erps.common.errorMessage.notBeforeMinimalDate", {fieldLabel: t("erps.availabledocuments.page.enddate"), minimalDate: minMoment.format("YYYY-MM-DD")}));
      if (maxMoment.isValid() && endMoment.isAfter(maxMoment))
        markError(validationResult, "endDate",
          t("erps.common.errorMessage.notAfterMaximalDate", {fieldLabel: t("erps.availabledocuments.page.enddate"), maximalDate: maxMoment.format("YYYY-MM-DD")}));
    }

    if (startMoment.isValid() && endMoment.isValid() && endMoment.isBefore(startMoment)) {
      markError(validationResult, ["startDate", "endDate"],
        t("erps.common.errorMessage.dateCompare", {earlierDate: t("erps.availabledocuments.page.startdate"), laterDate: t("erps.availabledocuments.page.enddate")}));
    }
    return validationResult;
  };

  function markError(validationResult, fieldName, errorText) {
    if (errorText) {
      validationResult.errors.push(errorText);
      if (fieldName) {
          if (Array.isArray(fieldName))
            fieldName.forEach(attr => validationResult.highlightError[attr] = true);
          else
            validationResult.highlightError[fieldName] = true;
      }
    }
  }

  const resetAvailableDocumentsHandler = (allSourceReplacement) => {
    setIsSearchValid(true);
    setHasSearch(false);
    setSearchCriteria({...initialStates});
    if (setErrorMessage) {
      setErrorMessage([]);
    }
    if (setHighlightError) {
      setHighlightError({});
    }

    const searchBillerFilesDto = {
      ...actualRequest,  // this sets biller_id, biller_name, limit
      start_date: "",
      end_date: "",
      selected_fi_number: "0",
      start_off_set: 0,
      source: allSourceReplacement || ""
    };
    setActualRequest(searchBillerFilesDto);

    if (setPageNumber)
      setPageNumber(0);

    dispatch(
      actions.availableDocumentActions.getAvailableDocument(searchBillerFilesDto)
    );
  };

  const handleAvailableReportsSearch = () => {
    let validationResult;
    if (searchCriteria?.startDate || searchCriteria?.endDate || searchCriteria?.reportName) {
      setIsSearchValid(true);
      validationResult = validateStartEndDates(searchCriteria?.startDate, searchCriteria?.endDate);
    }
    if (extraInputValiation)
      extraInputValiation(validationResult);
    if (hasSearchValue()) {
      setIsSearchValid(false);
      setSearchResults(null);
      setErrorMessage([]);
      setHighlightError(getEmptyCriteriaHighlightErrors(getSearchValues()));
    } else if (validationResult?.errors.length) {
      setErrorMessage(validationResult?.errors);
      setHighlightError(validationResult?.highlightError);
    } else {
      setIsSearchValid(true);
      setErrorMessage([]);
      setHighlightError({});
      if (setLoading) {
        setLoading(true);
      }
      setHasSearch(true);
      searchAvailableReportsByParams({ ...sanitizeQueryParams(searchCriteria), offset: 0, limit: actualRequest.limit });
    }
  };

  const searchAvailableReportsByParams = (queryParams) => {
    const searchBillerFilesDto = {
      start_date: formatDateOrReplace(queryParams.startDate, "YYYY-MM-DD"),
      end_date: formatDateOrReplace(queryParams.endDate, "YYYY-MM-DD"),
      start_off_set: 0,
      limit: queryParams.limit
    };
    if (queryParams.fiCompanyNumber)
      searchBillerFilesDto.fi_company_number = queryParams.fiCompanyNumber;
    if (setPageNumber) {
      setPageNumber(0);
    }

    if (roleId == 1 || roleId == 2)
      searchBillerFilesDto.biller_id = 0;
    
    if (queryParams.isWebportalReport) {
      if (queryParams.reportName)
        searchBillerFilesDto.source = queryParams.reportName;
    } else {
      searchBillerFilesDto.languageCode = loginUser?.languageCode;
      if (queryParams.reportName)
        searchBillerFilesDto.reportName = queryParams.reportName;
    }
    setActualRequest(searchBillerFilesDto);

    loadAvailableReports(searchBillerFilesDto, queryParams);
  };

  const loadAvailableReports = (apiRequest, queryParams) => {

    if (setLoading)
      setLoading(true);
    //Do I need these 3 lines below ??
    // if (setInitialLoading)
    //   setInitialLoading(true);
    // setHasSearch(true);

    if (queryParams.isWebportalReport) {
      dispatch(
        actions.availableDocumentActions.getAvailableDocument(apiRequest, setLoading)
      );
    } else {
      apiRequest.languageCode = loginUser?.languageCode;
      if (queryParams.reportName)
        apiRequest.reportName = queryParams.reportName;

      setActualRequest(apiRequest);
      dispatch(
        actions.availableDocumentActions.getAvailableReports(apiRequest, setLoading)
      );
    }
  };

  const paginateAvailableReports = (paginationParams) => {
    standardPaginate(paginationParams);
    loadAvailableReports(actualRequest);
  };

  const resetAvailableReportsHandler = () => {
    setIsSearchValid(true);
    setSearchCriteria(initialStates);

    setActualRequest({
      ...actualRequest,  // this sets limit and whatever other properties that don't need resetting
      reportName: "",
      start_date: "",
      end_date: "",
      start_off_set: 0
    });

    if (setPageNumber) {
      setPageNumber(0);
    }
    setErrorMessage([]);
    setHighlightError({});
    setHasSearch(false);
    dispatch({ type: availableDocumentTypes.RESET_AVAILABLE_REPORTS_LIST });
  };

  const handlePricePlanSearch = () => {
    let errors = [];
    if (hasSuccessMessageAvailable) {
      handleCloseSuccessMessage();
    }
    if (hasSearchValue()) {
      setErrorMssg(errors);
      setIsSearchValid(false);
      setSearchResults(null);
      setFieldErrors(getEmptyCriteriaHighlightErrors(getSearchValues()));
    } else {
      setIsSearchValid(true);
      setFieldErrors({});
      if (setLoading) {
        setLoading(true);
      }
      searchPricePlanByParams({ ...userDetails });
    }
  };

  const searchManageUserDetailsByParams = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });

    let errors = [];
    let highlightErrorV1 = {};
    // const userId = actualCriteria.userId ? actualCriteria.userId : "";
    const { firstName, lastName, userId } = actualCriteria;

    if (userId && (firstName || lastName)) {
      errors.push(t("erps.manageusers.error.enteruseridorname"));
      highlightErrorV1 = { ...highlightErrorV1, userId: true, firstName: true, lastName: true };
    }

    let errorMsg = checkFieldValue(userId, "erps.manageusers.label.userid", false, (v) => Regex.alphaNumeric.test(v), (FI.CTB_BILLER == loginUser?.fiNumber || centralizedBillerId)? 13 : 12);
    if (errorMsg !== "") {
      errors.push(errorMsg);
      highlightErrorV1 = { ...highlightErrorV1, userId: true };
    }
    errorMsg = checkFieldValue(firstName, "erps.manageusers.label.firstname", false, (v) => Regex.firstLastName.test(v), 25);
    if (errorMsg !== "") {
      errors.push(errorMsg);
      highlightErrorV1 = { ...highlightErrorV1, firstName: true };
    }
    errorMsg = checkFieldValue(lastName, "erps.manageusers.label.lastname", false, (v) => Regex.firstLastName.test(v), 25);
    if (errorMsg !== "") {
      errors.push(errorMsg);
      highlightErrorV1 = { ...highlightErrorV1, lastName: true };
    }

    if (errors && errors.length) {
      setErrorMessage(errors);
      setFieldErrors(highlightErrorV1);
    } else {
      if (errorMessage && errorMessage.length) {
        setErrorMessage([]);
      }
      setFieldErrors({});
      if (setLoading) {
        setLoading(true);
      }
      const roleList = userRoleList?.map(({ role_type }) => role_type);
      dispatch(
        actions.userActions.getUserDetails({
          count: 0,
          ...actualCriteria,
          startOffset: 0,
          endOffset: 25,
          roleList,
          setLoading,
          t,
          billerId,
          centralizedBillerId,
        })
      );
    }
  };
  const handleManageUserDetailsSearch = () => {
    if (hasSuccessMessageAvailable) {
      handleCloseSuccessMessage();
    }
    if (hasSearchValue()) {
      setIsSearchValid(false);
      setSearchResults(null);
      setFieldErrors(getEmptyCriteriaHighlightErrors(getSearchValues()));
    } else {
      setIsSearchValid(true);
      setFieldErrors({});
      searchManageUserDetailsByParams({ ...userDetails });
    }
  };

  const paginateManageUsers = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const { firstName, lastName, userId } = userDetails;
    const roleList = userRoleList?.map(({ role_type }) => role_type);
    const searchRequest = {
      count: actualCriteria.count,
      startOffset: actualCriteria.startOffset,
      endOffset: actualCriteria.endOffset,
      roleList,
      firstName,
      lastName,
      userId,
      t,
      billerId,
      centralizedBillerId,
    };

    if (actualCriteria.startOffset < actualCriteria.count) {
      setManageUserState((prevState) => ({
        ...prevState,
        startOffset: actualCriteria.startOffset,
        endOffset: actualCriteria.endOffset,
      }));
      setInitialLoading(true);
      dispatch(
        actions.userActions.getUserDetails({
          ...searchRequest,
          setLoading: setInitialLoading,
        })
      );
    }
  };

  const paginatePricePlan = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    if (actualCriteria.startOffset < pricePlaneResult?.data.length) {
      const rowPerPage = actualCriteria.endOffset - actualCriteria.startOffset;
      const pageNumber =
        actualCriteria.endOffset /
          (actualCriteria.endOffset - actualCriteria.startOffset) -
        1;
      setPageNumber(pageNumber);
      setRowPerPage(rowPerPage);
      const updatedData = pricePlaneResult?.data
        .slice(pageNumber * rowPerPage, (pageNumber + 1) * rowPerPage)
        .map(({ billingModelNumber, ...rest }) => ({
          ...rest,
          billingModelNumber,
          id: billingModelNumber,
        }));
      setPricePlaneData(updatedData);
    }
  };

  const handleSearch = () => {
    if (hasSearchValue()) {
      setIsSearchValid(false);
      setSearchResults(null);
    } else {
      setIsSearchValid(true);

      //TODO - Below if condition to be removed [Once actual api is available]
      //if (checkSearchValue()) {
      initiateSearch({ ...searchCriteria });
      //} else {
      //setSearchResults({ count: 0, rows: [] });
      //}
    }
  };

  return {
    // handleSearch,
    searchCriteriaChangeHandler,
    searchCriteriaBlurHandler,
    resetHandler,
    isSearchValid,
    searchResults,
    initiateSearch,
    searchCriteria,
    actualRequest,
    paginateBudgetCategories,
    handleBudgetCategorySearch,
    resetBudgetCategoryHandler,
    handleManageUserDetailsSearch,
    paginateManageUsers,
    handlePricePlanSearch,
    resetPricePlanHandler,
    handleAvailableDocumentsSearch,
    paginateAvailableDocuments,
    searchAvailableDocumentsByParams,
    resetAvailableDocumentsHandler,
    handleBillerProfileSearch,
    resetBillerProfileHandler,
    paginateBillerProfilesList,
    handleAvailableReportsSearch,
    resetAvailableReportsHandler,
    paginateAvailableReports,
    paginatePricePlan,
    hasSearch,
    setActualRequest,
    fieldErrors
  };
};
